import React, { useState, useEffect } from 'react';
import { auth } from '../../lib/firebase';
import { notifyToast } from '../../lib/toastStore';
import { workspaceHeader } from '../../lib/workspaces';
import { useWorkspaces } from '../shared/WorkspaceProvider';
import Modal from '../shared/Modal';

/**
 * Every request this manager makes goes through here so the ACTIVE
 * workspace header is always attached (AGENTS.md rule). Follow-up
 * Item 7: the queue fetch used a raw fetch() with no X-Workspace-Id, so
 * the server resolved the DEFAULT workspace and getOrgCollectionDocsMerged
 * only merged that one context — a fresh org application stamped with
 * the admin's active (non-default) workspace never appeared in Pending
 * Approvals, while older default-stamped records showed in both lists.
 */
function adminFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: { ...workspaceHeader(), ...((options?.headers as Record<string, string>) || {}) },
  });
}
import {
  buildApprovalSnapshot,
  ENHANCED_ACCESS_DESCRIPTION,
  ENHANCED_ACCESS_BULLETS,
  type ApprovalSnapshot,
} from '../../lib/onboardingUx';
import {
  CheckCircle,
  XCircle,
  RefreshCw,
  Building2,
  User,
  Search,
  AlertCircle,
  Check,
  X,
  ExternalLink,
  Eye,
  Shield,
  Globe,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  Award,
  FileText,
  Filter,
  History,
  Lock,
  RotateCcw,
  AlertTriangle,
  Briefcase,
} from 'lucide-react';

interface PendingApprovalsManagerProps {
  canEdit?: boolean;
}

/** Compact completeness bar + risk-flag badges for a pending-approval row. */
function SnapshotBadges({ snapshot }: { snapshot: ApprovalSnapshot }) {
  const pctColor = snapshot.pct === 100 ? 'bg-emerald-500' : snapshot.pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  const flagLabel: Record<string, string> = {
    'duplicate-name': 'Duplicate name',
    'disposable-email': 'Free email',
    'missing-fields': 'Missing fields',
  };
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <span
        className="flex items-center gap-1.5"
        title={`${snapshot.filled} of ${snapshot.total} required fields filled`}
      >
        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-200 inline-block">
          <span className={`block h-full rounded-full ${pctColor}`} style={{ width: `${snapshot.pct}%` }} />
        </span>
        <span className="text-[10px] font-bold text-slate-500">{snapshot.pct}%</span>
      </span>
      {snapshot.flags.map(f => (
        <span
          key={f.code}
          title={f.label}
          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
            f.severity === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
          }`}
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          {flagLabel[f.code] || f.code}
        </span>
      ))}
    </div>
  );
}

export default function PendingApprovalsManager({ canEdit = true }: PendingApprovalsManagerProps = {}) {
  const [pendingEmployers, setPendingEmployers] = useState<any[]>([]);
  const [pendingSponsors, setPendingSponsors] = useState<any[]>([]);
  const [pendingReferrers, setPendingReferrers] = useState<any[]>([]);
  const [pendingJobs, setPendingJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'employer' | 'referrer' | 'sponsor' | 'job'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | '7d' | '30d'>('all');
  const [refreshKey, setRefreshKey] = useState(0);
  // Enhanced Referrer Access opt-in at approval time, keyed by referrer id.
  // DEFAULT OFF (unchecked) — the admin must explicitly check it to grant.
  const [enhancedOnApprove, setEnhancedOnApprove] = useState<Record<string, boolean>>({});
  // Full org-name index (pending + active) for the duplicate-name risk flag.
  const [orgNameIndex, setOrgNameIndex] = useState<{ employers: { id: string; name: string }[]; sponsors: { id: string; name: string }[]; referrers: { id: string; name: string }[] }>({
    employers: [],
    sponsors: [],
    referrers: [],
  });

  // Selected item modal & audit history state
  const [selectedItem, setSelectedItem] = useState<{ item: any; type: 'employer' | 'sponsor' | 'referrer' } | null>(null);
  const [auditHistory, setAuditHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Action modal (Reject / Deactivate with required reason)
  const [actionModal, setActionModal] = useState<{
    type: 'employer' | 'sponsor' | 'referrer';
    id: string;
    action: 'reject' | 'deactivate' | 'reactivate';
    title: string;
  } | null>(null);
  const [reasonInput, setReasonInput] = useState('');
  const [reasonError, setReasonError] = useState('');

  // Gate 1 — commission amounts that must be collected before a referrer
  // approval can proceed. The server blocks approval while any ASSIGNED
  // program has no amount set and names those programs; this modal
  // collects them and resubmits the approval with the amounts inline.
  const [rateGate, setRateGate] = useState<{
    id: string;
    name: string;
    missing: { id: string; name: string }[];
    /** Assigned programs that already have an amount — shown read-only
     *  for context so "which programs are missing" is unambiguous. */
    alreadySet: string[];
    amounts: Record<string, string>;
    /** Set after a submit attempt so empty fields show inline hints. */
    attempted: boolean;
  } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    notifyToast(type, message);
  };

  // Follow-up Item 12: never fetch before the workspace context has
  // resolved — the queue would read the default workspace and miss
  // records stamped with the admin's active workspace (see Item 7).
  const { loading: workspaceLoading, activeWorkspaceId } = useWorkspaces();

  useEffect(() => {
    if (workspaceLoading) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await adminFetch('/api/admin/pending-approvals', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (res.status === 404) {
          if (!cancelled) { setPendingEmployers([]); setPendingSponsors([]); setPendingReferrers([]); setPendingJobs([]); setLoading(false); }
          return;
        }
        if (!res.ok) {
          if (!cancelled) showToast(`Failed to load approvals (${res.status})`, 'error');
          if (!cancelled) { setPendingEmployers([]); setPendingSponsors([]); setPendingReferrers([]); setPendingJobs([]); setLoading(false); }
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setPendingEmployers(data.employers || []);
          setPendingSponsors(data.sponsors || []);
          setPendingReferrers(data.referrers || []);
          setPendingJobs(data.jobs || []);
          if (data.orgNameIndex) setOrgNameIndex(data.orgNameIndex);
        }
      } catch {
        if (!cancelled) {
          setPendingEmployers([]);
          setPendingSponsors([]);
          setPendingReferrers([]);
          setPendingJobs([]);
        }
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [refreshKey, workspaceLoading, activeWorkspaceId]);

  // Auto-refresh when window regains focus (kills the manual refresh need)
  useEffect(() => {
    const onFocus = () => setRefreshKey(k => k + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Fetch audit history when an item detail modal is opened
  useEffect(() => {
    if (!selectedItem) {
      setAuditHistory([]);
      return;
    }
    let cancelled = false;
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await adminFetch(`/api/admin/accounts/${selectedItem.type}/${selectedItem.item.id}/audit-history`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setAuditHistory(data.history || []);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [selectedItem]);

  const handleApprove = async (type: 'employer' | 'sponsor' | 'referrer', id: string, programRates?: Record<string, number>) => {
    setApproving(`${type}-${id}`);
    try {
      const token = await auth.currentUser?.getIdToken();
      // Enhanced Referrer Access is NEVER granted by default. Only include
      // the flag when the admin explicitly checked the opt-in (referrers
      // only); the server ignores it for all other types.
      const body: any = { status: 'approved' };
      if (type === 'referrer' && enhancedOnApprove[id] === true) {
        body.enhancedPermission = true;
      }
      // Gate 1: amounts collected in the rate-gate modal ride along; the
      // server applies them to the profile, then evaluates the gate.
      if (type === 'referrer' && programRates) {
        body.programRates = programRates;
      }
      const res = await adminFetch(`/api/admin/accounts/${type}/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (type === 'referrer' && Array.isArray(errData.missingPrograms) && errData.missingPrograms.length > 0) {
          const item = pendingReferrers.find(r => r.id === id);
          const missingNames = new Set(errData.missingPrograms.map((p: any) => p.name));
          const alreadySet = ((item as any)?.relatedPrograms || []).filter((n: string) => !missingNames.has(n));
          setRateGate({ id, name: item?.name || 'this referrer', missing: errData.missingPrograms, alreadySet, amounts: {}, attempted: false });
          return;
        }
        throw new Error(errData.error || 'Failed to approve');
      }
      setRateGate(null);
      if (type === 'employer') setPendingEmployers(prev => prev.filter(e => e.id !== id));
      else if (type === 'sponsor') setPendingSponsors(prev => prev.filter(s => s.id !== id));
      else setPendingReferrers(prev => prev.filter(r => r.id !== id));
      const label = type === 'employer' ? 'Company' : type === 'sponsor' ? 'Sponsor' : 'Referrer';
      showToast(`${label} approved successfully`, 'success');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setApproving(null);
    }
  };

  const submitRateGate = async () => {
    if (!rateGate) return;
    const programRates: Record<string, number> = {};
    for (const p of rateGate.missing) {
      const raw = (rateGate.amounts[p.id] ?? '').trim();
      const n = Number(raw);
      if (raw === '' || !isFinite(n) || n < 0) {
        // Inline hints take over (fields are marked in the modal); the
        // toast names the first offending program for screen-reader and
        // quick-scan clarity. The gate itself is unchanged — approval
        // still only proceeds with an amount for every missing program.
        setRateGate(prev => prev ? { ...prev, attempted: true } : prev);
        showToast(`Enter a commission amount for ${p.name} (an amount of $0 is allowed).`, 'error');
        return;
      }
      programRates[p.id] = n;
    }
    await handleApprove('referrer', rateGate.id, programRates);
  };

  const handleApproveJob = async (id: string) => {
    setApproving(`job-${id}`);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await adminFetch(`/api/admin/jobs/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'published' }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to publish job');
      }
      setPendingJobs(prev => prev.filter(j => j.id !== id));
      showToast('Job published successfully', 'success');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setApproving(null);
    }
  };

  const handleRejectJob = async (id: string) => {
    setApproving(`job-${id}`);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await adminFetch(`/api/admin/jobs/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'closed' }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to close job');
      }
      setPendingJobs(prev => prev.filter(j => j.id !== id));
      showToast('Job closed', 'success');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setApproving(null);
    }
  };

  const handleActionSubmit = async () => {
    if (!actionModal) return;
    const { type, id, action } = actionModal;
    if ((action === 'reject' || action === 'deactivate') && !reasonInput.trim()) {
      setReasonError('Reason is required for rejection or deactivation.');
      return;
    }
    setReasonError('');
    setApproving(`${type}-${id}`);
    try {
      const token = await auth.currentUser?.getIdToken();
      const targetStatus = action === 'reject' ? 'rejected' : action === 'deactivate' ? 'deactivated' : 'approved';
      const res = await adminFetch(`/api/admin/accounts/${type}/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: targetStatus, reason: reasonInput.trim() }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to ${action}`);
      }
      if (type === 'employer') setPendingEmployers(prev => prev.filter(e => e.id !== id));
      else if (type === 'sponsor') setPendingSponsors(prev => prev.filter(s => s.id !== id));
      else setPendingReferrers(prev => prev.filter(r => r.id !== id));

      const label = type === 'employer' ? 'Company' : type === 'sponsor' ? 'Sponsor' : 'Referrer';
      showToast(`${label} ${action}d successfully`, 'success');
      setActionModal(null);
      setReasonInput('');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setApproving(null);
    }
  };

  const filterItems = (items: any[]) => {
    let filtered = items;

    // Date filter
    if (dateFilter !== 'all') {
      const now = Date.now();
      const maxAgeMs = dateFilter === '7d' ? 7 * 24 * 3600 * 1000 : 30 * 24 * 3600 * 1000;
      filtered = filtered.filter(i => {
        if (!i.createdAt) return true;
        return now - new Date(i.createdAt).getTime() <= maxAgeMs;
      });
    }

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((i: any) => {
        const idStr = (i.employerId || i.referrerId || i.sponsorId || i.id || ('ID-' + (i.numericId || ''))).toLowerCase();
        return (
          idStr.includes(q) ||
          (i.companyName || i.name || '').toLowerCase().includes(q) ||
          (i.contactName || '').toLowerCase().includes(q) ||
          (i.contactEmail || i.email || '').toLowerCase().includes(q)
        );
      });
    }

    return filtered;
  };

  const filteredEmployers = roleFilter === 'all' || roleFilter === 'employer' ? filterItems(pendingEmployers) : [];
  const filteredSponsors = roleFilter === 'all' || roleFilter === 'sponsor' ? filterItems(pendingSponsors) : [];
  const filteredReferrers = roleFilter === 'all' || roleFilter === 'referrer' ? filterItems(pendingReferrers) : [];
  const filteredJobs = roleFilter === 'all' || roleFilter === 'job' ? pendingJobs.filter((j: any) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (j.title || '').toLowerCase().includes(q) || (j.employerName || '').toLowerCase().includes(q);
  }) : [];

  const total = pendingEmployers.length + pendingSponsors.length + pendingReferrers.length + pendingJobs.length;

  /** Approval snapshot (completeness + risk flags) for one pending record. */
  const snapshotFor = (type: 'employer' | 'sponsor' | 'referrer', item: any): ApprovalSnapshot =>
    buildApprovalSnapshot(type, item, (orgNameIndex as any)[`${type}s`] || []);

  return (
    <div className="p-4 md:p-6 space-y-6 font-sans">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Pending Approvals</h1>
          <p className="text-xs text-slate-500">
            {total} pending {total === 1 ? 'approval' : 'approvals'} — {pendingEmployers.length} employer{pendingEmployers.length !== 1 ? 's' : ''}, {pendingSponsors.length} sponsor{pendingSponsors.length !== 1 ? 's' : ''}, {pendingReferrers.length} referrer{pendingReferrers.length !== 1 ? 's' : ''}, {pendingJobs.length} job{pendingJobs.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition cursor-pointer"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-2xl shadow-sm ring-1 ring-slate-100">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by name, contact, or email..."
            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
          />
        </div>

        {/* Role Filter Tabs */}
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
          {[
            { id: 'all' as const, label: 'All Roles' },
            { id: 'employer' as const, label: 'Employers' },
            { id: 'referrer' as const, label: 'Referrers' },
            { id: 'sponsor' as const, label: 'Sponsors' },
            { id: 'job' as const, label: `Jobs${pendingJobs.length > 0 ? ` (${pendingJobs.length})` : ''}` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setRoleFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                roleFilter === tab.id ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Date Filter */}
        <div className="flex items-center gap-2 text-xs">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <select
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value as any)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          >
            <option value="all">All Submission Dates</option>
            <option value="7d">Submitted Last 7 Days</option>
            <option value="30d">Submitted Last 30 Days</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="premium-loader mx-auto" />
          <p className="text-xs text-slate-500 mt-3">Loading pending approvals...</p>
        </div>
      ) : searchQuery && filteredEmployers.length === 0 && filteredSponsors.length === 0 && filteredReferrers.length === 0 && filteredJobs.length === 0 && total > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-8 text-center">
          <Search className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No results matching "{searchQuery}"</p>
        </div>
      ) : filteredEmployers.length === 0 && filteredSponsors.length === 0 && filteredReferrers.length === 0 && filteredJobs.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-12 text-center">
          <CheckCircle className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700">All caught up!</p>
          <p className="text-xs text-slate-500 mt-1">No pending employer, referrer, sponsor, or job approvals.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Pending Employers */}
          {filteredEmployers.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-brand" />
                  <h2 className="text-sm font-bold text-slate-900">Employers Pending Approval</h2>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px] font-bold">{pendingEmployers.length}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-semibold">
                    <tr>
                      <th className="px-4 py-3 text-left">Company</th>
                      <th className="px-4 py-3 text-left">Contact</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Phone</th>
                      <th className="px-4 py-3 text-left">Programs</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredEmployers.map((emp: any) => (
                      <tr key={emp.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setSelectedItem({ item: emp, type: 'employer' })}
                            className="text-left group cursor-pointer"
                          >
                            <p className="font-semibold text-slate-900 group-hover:text-blue-600 group-hover:underline flex items-center gap-1.5">
                              {emp.companyName || emp.name || ''}
                              <Eye className="h-3.5 w-3.5 text-slate-400 group-hover:text-blue-600" />
                            </p>
                            <span className="text-[10px] font-mono text-brand bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded font-bold mt-0.5 inline-block">
                              {emp.employerId || (emp.numericId ? `EMP-${emp.numericId}` : '—')}
                            </span>
                            <SnapshotBadges snapshot={snapshotFor('employer', emp)} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{emp.contactName || ''}</td>
                        <td className="px-4 py-3 text-slate-600">{emp.contactEmail || emp.email || ''}</td>
                        <td className="px-4 py-3 text-slate-600">{emp.contactPhone || emp.phone || ''}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(emp.relatedPrograms || []).map((p: string) => (
                              <span key={p} className="px-1.5 py-0.5 bg-brand-active rounded text-[10px] font-semibold">{p}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setSelectedItem({ item: emp, type: 'employer' })}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                            >
                              <Eye className="h-3.5 w-3.5 text-slate-500" /> Details
                            </button>
                            {canEdit && (
                              <>
                                <button
                                  onClick={() => handleApprove('employer', emp.id)}
                                  disabled={approving === `employer-${emp.id}`}
                                  className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:bg-success/40 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                                >
                                  <Check className="h-3.5 w-3.5" /> Approve
                                </button>
                                <button
                                  onClick={() => setActionModal({ type: 'employer', id: emp.id, action: 'reject', title: `Reject ${emp.companyName || 'Employer'}` })}
                                  disabled={approving === `employer-${emp.id}`}
                                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                                >
                                  <X className="h-3.5 w-3.5" /> Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pending Sponsors */}
          {filteredSponsors.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-cyan-600" />
                  <h2 className="text-sm font-bold text-slate-900">Sponsors Pending Approval</h2>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px] font-bold">{pendingSponsors.length}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-semibold">
                    <tr>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Tier</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">SEVIS Program</th>
                      <th className="px-4 py-3 text-left">Categories</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredSponsors.map((sp: any) => (
                      <tr key={sp.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          <button
                            type="button"
                            onClick={() => setSelectedItem({ item: sp, type: 'sponsor' })}
                            className="text-left group cursor-pointer"
                          >
                            <p className="font-semibold text-slate-900 group-hover:text-cyan-600 group-hover:underline flex items-center gap-1.5">
                              {sp.name || sp.companyName || ''}
                              <Eye className="h-3.5 w-3.5 text-slate-400 group-hover:text-cyan-600" />
                            </p>
                            <SnapshotBadges snapshot={snapshotFor('sponsor', sp)} />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-brand-active uppercase">{sp.tier || 'rti'}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{sp.contactEmail || ''}</td>
                        <td className="px-4 py-3 text-slate-600 font-mono text-xs">{sp.sevisProgramNumber || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(sp.supportedCategories || []).map((c: string) => (
                              <span key={c} className="px-1.5 py-0.5 bg-cyan-50 text-cyan-700 rounded text-[10px] font-semibold">{c}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setSelectedItem({ item: sp, type: 'sponsor' })}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                            >
                              <Eye className="h-3.5 w-3.5 text-slate-500" /> Details
                            </button>
                            {canEdit && (
                              <>
                                <button
                                  onClick={() => handleApprove('sponsor', sp.id)}
                                  disabled={approving === `sponsor-${sp.id}`}
                                  className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:bg-success/40 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                                >
                                  <Check className="h-3.5 w-3.5" /> Approve
                                </button>
                                <button
                                  onClick={() => setActionModal({ type: 'sponsor', id: sp.id, action: 'reject', title: `Reject ${sp.name || 'Sponsor'}` })}
                                  disabled={approving === `sponsor-${sp.id}`}
                                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                                >
                                  <X className="h-3.5 w-3.5" /> Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pending Referrers */}
          {filteredReferrers.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-purple-600" />
                  <h2 className="text-sm font-bold text-slate-900">Referrers Pending Approval</h2>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px] font-bold">{pendingReferrers.length}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-semibold">
                    <tr>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Contact</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Phone</th>
                      <th className="px-4 py-3 text-left">Programs</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredReferrers.map((ref: any) => (
                      <tr key={ref.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setSelectedItem({ item: ref, type: 'referrer' })}
                            className="text-left group cursor-pointer"
                          >
                            <p className="font-semibold text-slate-900 group-hover:text-purple-600 group-hover:underline flex items-center gap-1.5">
                              {ref.name || ''}
                              <Eye className="h-3.5 w-3.5 text-slate-400 group-hover:text-purple-600" />
                            </p>
                            <span className="text-[10px] font-mono text-purple-700 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded font-bold mt-0.5 inline-block">
                              {ref.inviteCode || '—'}
                            </span>
                            <SnapshotBadges snapshot={snapshotFor('referrer', ref)} />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${(ref as any).profileType === 'company' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-700'}`}>
                            {(ref as any).profileType === 'company' ? 'Company / Agency' : 'Individual Referrer'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{ref.contactName || ''}</td>
                        <td className="px-4 py-3 text-slate-600">{ref.contactEmail || ref.email || ''}</td>
                        <td className="px-4 py-3 text-slate-600">{ref.contactPhone || ref.phone || ''}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(ref.relatedPrograms || []).map((p: string) => (
                              <span key={p} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-semibold">{p}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setSelectedItem({ item: ref, type: 'referrer' })}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                            >
                              <Eye className="h-3.5 w-3.5 text-slate-500" /> View Details
                            </button>
                            {canEdit && (
                              <>
                                <label
                                  className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-slate-600 cursor-pointer select-none"
                                  title="Grant Enhanced Referrer Access on approval (off by default)"
                                >
                                  <input
                                    type="checkbox"
                                    checked={enhancedOnApprove[ref.id] === true}
                                    onChange={(e) => setEnhancedOnApprove(prev => ({ ...prev, [ref.id]: e.target.checked }))}
                                    disabled={approving === `referrer-${ref.id}`}
                                    className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 cursor-pointer"
                                  />
                                  Enhanced access
                                </label>
                                <button
                                  onClick={() => handleApprove('referrer', ref.id)}
                                  disabled={approving === `referrer-${ref.id}`}
                                  className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:bg-success/40 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                                >
                                  <Check className="h-3.5 w-3.5" /> Approve
                                </button>
                                <button
                                  onClick={() => setActionModal({ type: 'referrer', id: ref.id, action: 'reject', title: `Reject ${ref.name || 'Referrer'}` })}
                                  disabled={approving === `referrer-${ref.id}`}
                                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                                >
                                  <X className="h-3.5 w-3.5" /> Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pending Jobs */}
          {filteredJobs.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-brand" />
                  <h2 className="text-sm font-bold text-slate-900">Jobs Pending Review</h2>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px] font-bold">{pendingJobs.length}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-left">
                      <th className="px-5 py-3 font-semibold">Job Title</th>
                      <th className="px-5 py-3 font-semibold">Employer</th>
                      <th className="px-5 py-3 font-semibold">Program</th>
                      <th className="px-5 py-3 font-semibold">Location</th>
                      <th className="px-5 py-3 font-semibold">Posted</th>
                      <th className="px-5 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredJobs.map((job: any) => (
                      <tr key={job.id} className="hover:bg-slate-50">
                        <td className="px-5 py-3 font-semibold text-slate-900">{job.title || 'Untitled Job'}</td>
                        <td className="px-5 py-3 text-slate-600">{job.employerName || job.companyName || '—'}</td>
                        <td className="px-5 py-3 text-slate-600">{job.programName || job.programId || '—'}</td>
                        <td className="px-5 py-3 text-slate-600">{job.location || job.city || '—'}</td>
                        <td className="px-5 py-3 text-slate-500">{job.createdAt ? new Date(job.createdAt).toLocaleDateString() : '—'}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleApproveJob(job.id)}
                              disabled={approving === `job-${job.id}`}
                              className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:bg-success/40 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                            >
                              <Check className="h-3.5 w-3.5" /> Publish
                            </button>
                            <button
                              onClick={() => handleRejectJob(job.id)}
                              disabled={approving === `job-${job.id}`}
                              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1"
                            >
                              <X className="h-3.5 w-3.5" /> Close
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal with Profile & Audit History */}
      {selectedItem && (
        <Modal title="Pending approval details">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl space-y-6 relative border border-slate-100 animate-fade-in max-h-[90vh] overflow-y-auto">
            <button aria-label="Close" title="Close"
              onClick={() => setSelectedItem(null)}
              className="absolute top-5 right-5 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-2xl shrink-0 ${
                selectedItem.type === 'referrer' ? 'bg-purple-100 text-purple-700' :
                selectedItem.type === 'employer' ? 'bg-blue-100 text-blue-700' : 'bg-cyan-100 text-cyan-700'
              }`}>
                {selectedItem.type === 'referrer' ? <User className="h-6 w-6" /> : <Building2 className="h-6 w-6" />}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${
                    selectedItem.type === 'referrer' ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                    selectedItem.type === 'employer' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                  }`}>
                    {selectedItem.type === 'referrer' ? ((selectedItem.item as any).profileType === 'company' ? 'Company / Agency Referrer' : 'Individual Referrer') : selectedItem.type}
                  </span>
                  <span className="px-2.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-bold uppercase">
                    Status: {selectedItem.item.status || 'pending'}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-slate-900 mt-1">
                  {selectedItem.item.companyName || selectedItem.item.name || 'Unnamed Record'}
                </h2>
                <p className="text-xs font-mono text-slate-400 mt-0.5">
                  ID: {selectedItem.item.employerId || selectedItem.item.referrerId || selectedItem.item.sponsorId || selectedItem.item.id}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-y border-slate-100 py-5 text-sm">
              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> Primary Contact Person</span>
                <p className="font-medium text-slate-800">{selectedItem.item.contactName || selectedItem.item.contactPerson || 'Not provided'}</p>
              </div>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email Address</span>
                <p className="font-medium text-slate-800 truncate">{selectedItem.item.contactEmail || selectedItem.item.email || 'Not provided'}</p>
              </div>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Phone Number</span>
                <p className="font-medium text-slate-800">{selectedItem.item.contactPhone || selectedItem.item.phone || 'Not provided'}</p>
              </div>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" /> Website</span>
                {selectedItem.item.website ? (
                  <a href={selectedItem.item.website.startsWith('http') ? selectedItem.item.website : `https://${selectedItem.item.website}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline font-medium flex items-center gap-1 truncate">
                    {selectedItem.item.website} <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                ) : (
                  <p className="font-medium text-slate-800">Not provided</p>
                )}
              </div>

              <div className="space-y-1 sm:col-span-2">
                <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Address / Location</span>
                <p className="font-medium text-slate-800">{selectedItem.item.address || selectedItem.item.companyAddress || 'Not provided'}</p>
              </div>

              {selectedItem.type === 'referrer' && (
                <>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Tax ID / Business Reg</span>
                    <p className="font-medium text-slate-800 font-mono">{selectedItem.item.taxId || 'Not provided'}</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><Award className="h-3.5 w-3.5" /> Referral Specialty</span>
                    <p className="font-medium text-slate-800">{selectedItem.item.referralSpecialty || 'General Recruitment'}</p>
                  </div>
                </>
              )}

              <div className="space-y-1 sm:col-span-2">
                <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Related Programs / Categories</span>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {(selectedItem.item.relatedPrograms || selectedItem.item.supportedCategories || []).length > 0 ? (
                    (selectedItem.item.relatedPrograms || selectedItem.item.supportedCategories).map((p: string) => (
                      <span key={p} className="px-2 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-semibold">{p}</span>
                    ))
                  ) : (
                    <span className="text-slate-400 text-xs italic">None specified</span>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Submission Date</span>
                <p className="font-medium text-slate-800">
                  {selectedItem.item.createdAt ? new Date(selectedItem.item.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown'}
                </p>
              </div>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Linked Account UID</span>
                <p className="font-medium text-slate-800 font-mono text-xs truncate">{selectedItem.item.userId || 'Unlinked'}</p>
              </div>
            </div>

            {/* Approval snapshot: completeness + risk flags */}
            {(() => {
              const snap = snapshotFor(selectedItem.type, selectedItem.item);
              const barColor = snap.pct === 100 ? 'bg-emerald-500' : snap.pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
              return (
                <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Approval snapshot</h3>
                    <span className="text-xs font-bold text-slate-700">{snap.filled}/{snap.total} fields · {snap.pct}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div className={`h-full rounded-full ${barColor}`} style={{ width: `${snap.pct}%` }} />
                  </div>
                  {snap.missing.length > 0 && (
                    <p className="text-xs text-red-700"><strong>Missing required fields:</strong> {snap.missing.join(', ')}</p>
                  )}
                  {snap.flags.map(f => (
                    <p
                      key={f.code}
                      className={`flex items-start gap-1.5 text-xs font-semibold ${f.severity === 'error' ? 'text-red-700' : 'text-amber-800'}`}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden="true" />
                      {f.label}
                    </p>
                  ))}
                  {snap.flags.length === 0 && (
                    <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5" aria-hidden="true" /> No risk flags.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Audit History Timeline */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                <History className="h-3.5 w-3.5 text-slate-400" /> Account Status History
              </h3>
              {historyLoading ? (
                <div className="text-xs text-slate-500 flex items-center gap-2 py-2">
                  <RefreshCw className="h-3 w-3 animate-spin" /> Loading status history...
                </div>
              ) : auditHistory.length === 0 ? (
                <p className="text-xs text-slate-500 italic bg-slate-50 p-3 rounded-xl">No status history recorded yet.</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                  {auditHistory.map((log: any) => (
                    <div key={log.id} className="p-3 bg-slate-50 rounded-xl text-xs space-y-1 border border-slate-100">
                      <div className="flex items-center justify-between font-semibold text-slate-800">
                        <span>{log.fromStatus ? `${log.fromStatus} → ${log.toStatus}` : log.action}</span>
                        <span className="text-[11px] text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                      {log.reason && <p className="text-slate-600 italic">"{log.reason}"</p>}
                      {log.actorEmail && <p className="text-[11px] text-slate-500">By {log.actorEmail}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Enhanced Referrer Access — prominent opt-in with plain-English grant description.
                DEFAULT OFF: the admin must explicitly check it to grant. */}
            {selectedItem.type === 'referrer' && canEdit && (
              <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/70 p-4">
                <label className="flex cursor-pointer select-none items-start gap-3">
                  <input
                    type="checkbox"
                    checked={enhancedOnApprove[selectedItem.item.id] === true}
                    onChange={(e) => setEnhancedOnApprove(prev => ({ ...prev, [selectedItem.item.id]: e.target.checked }))}
                    disabled={approving === `referrer-${selectedItem.item.id}`}
                    className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-slate-300 text-indigo-600"
                  />
                  <span>
                    <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-900">
                      Grant Enhanced Referrer Access
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
                        Off by default
                      </span>
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-slate-600">
                      {ENHANCED_ACCESS_DESCRIPTION}
                    </span>
                    <ul className="mt-2 space-y-1">
                      {ENHANCED_ACCESS_BULLETS.map(b => (
                        <li key={b} className="flex items-start gap-1.5 text-[11px] font-medium text-slate-600">
                          <Check className="mt-0.5 h-3 w-3 shrink-0 text-indigo-600" aria-hidden="true" /> {b}
                        </li>
                      ))}
                    </ul>
                  </span>
                </label>
              </div>
            )}

            {canEdit && (
              <div className="flex items-center justify-end gap-3 pt-2">
                {selectedItem.item.status === 'active' || selectedItem.item.status === 'approved' ? (
                  <button
                    type="button"
                    onClick={() => {
                      const { type, item } = selectedItem;
                      setSelectedItem(null);
                      setActionModal({ type, id: item.id, action: 'deactivate', title: `Deactivate ${item.companyName || item.name || 'Account'}` });
                    }}
                    className="px-4 py-2.5 bg-slate-600 hover:bg-slate-700 text-white rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-sm"
                  >
                    <Lock className="h-4 w-4" /> Deactivate Account
                  </button>
                ) : selectedItem.item.status === 'deactivated' ? (
                  <button
                    type="button"
                    onClick={() => {
                      const { type, item } = selectedItem;
                      setSelectedItem(null);
                      setActionModal({ type, id: item.id, action: 'reactivate', title: `Reactivate ${item.companyName || item.name || 'Account'}` });
                    }}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-sm"
                  >
                    <RotateCcw className="h-4 w-4" /> Reactivate Account
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={() => {
                    const { type, item } = selectedItem;
                    setSelectedItem(null);
                    setActionModal({ type, id: item.id, action: 'reject', title: `Reject ${item.companyName || item.name || 'Account'}` });
                  }}
                  className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-sm"
                >
                  <X className="h-4 w-4" /> Reject Profile
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const { type, item } = selectedItem;
                    setSelectedItem(null);
                    await handleApprove(type, item.id);
                  }}
                  disabled={approving === `${selectedItem.type}-${selectedItem.item.id}`}
                  className="px-5 py-2.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-md shadow-success/20"
                >
                  <Check className="h-4 w-4" /> Approve Profile
                </button>
              </div>
            )}
          </div>
      </Modal>
      )}

      {/* Action Modal (Reject / Deactivate Reason) */}
      {actionModal && (
        <Modal title={actionModal.title}>
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100 animate-fade-in">
            <h2 className="text-base font-bold text-slate-900">{actionModal.title}</h2>
            <p className="text-xs text-slate-500 leading-relaxed">
              {actionModal.action === 'reject'
                ? 'Please provide a clear reason for rejecting this application. This reason will be included in the notification email.'
                : actionModal.action === 'deactivate'
                ? 'Please provide a reason for deactivating this account. The user\'s active session will be invalidated.'
                : 'Confirm reactivating this account.'}
            </p>

            {(actionModal.action === 'reject' || actionModal.action === 'deactivate') && (
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Reason <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span></label>
                <textarea
                  value={reasonInput}
                  onChange={e => { setReasonInput(e.target.value); if (reasonError) setReasonError(''); }}
                  placeholder="Enter detailed reason..."
                  rows={3}
                  className="w-full p-3 text-xs border border-slate-300 rounded-xl focus:outline-none focus:border-brand bg-white"
                />
                {reasonError && <p className="text-xs text-red-600">{reasonError}</p>}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => { setActionModal(null); setReasonInput(''); setReasonError(''); }}
                className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl text-xs font-semibold transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleActionSubmit}
                disabled={approving === `${actionModal.type}-${actionModal.id}`}
                className={`px-4 py-2 text-white rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                  actionModal.action === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                  actionModal.action === 'deactivate' ? 'bg-slate-600 hover:bg-slate-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Confirm {actionModal.action.charAt(0).toUpperCase() + actionModal.action.slice(1)}
              </button>
            </div>
          </div>
      </Modal>
      )}

      {/* Gate 1 Modal — collect commission amounts required before approval */}
      {rateGate && (() => {
        // §5.1: error prevention in the §2 pattern — the approve action
        // stays disabled until every missing program has a valid amount,
        // with a live count of what remains. The server gate is untouched.
        const remaining = rateGate.missing.filter(p => {
          const raw = (rateGate.amounts[p.id] ?? '').trim();
          return raw === '' || !isFinite(Number(raw)) || Number(raw) < 0;
        }).length;
        const allValid = remaining === 0;
        return (
        <Modal title="Commission amounts required">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100 animate-fade-in max-h-[90dvh] overflow-y-auto">
            <h2 className="text-base font-bold text-slate-900">Commission amounts required</h2>
            <p className="text-xs text-slate-500 leading-relaxed">
              <strong>{rateGate.name}</strong> can't be approved yet: no commission amount has been set for the
              program(s) below. Enter an amount for each one — an amount of $0 is allowed and simply means no
              commission is earned for that program.
            </p>
            <p className="text-[11px] font-bold text-slate-600" role="status">
              {allValid
                ? 'All assigned programs have an amount — ready to approve.'
                : `${remaining} of ${rateGate.missing.length} missing program${rateGate.missing.length === 1 ? '' : 's'} still need${remaining === 1 ? 's' : ''} an amount.`}
            </p>
            {rateGate.alreadySet.length > 0 && (
              <div className="space-y-1.5">
                {rateGate.alreadySet.map(name => (
                  <div key={name} className="flex items-center justify-between gap-3 px-3 py-2 bg-emerald-50/60 border border-emerald-100 rounded-xl">
                    <span className="text-xs font-semibold text-slate-600">{name}</span>
                    <span className="text-[10px] font-bold text-emerald-700 shrink-0">Amount already set</span>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              {rateGate.missing.map(p => {
                const raw = (rateGate.amounts[p.id] ?? '').trim();
                const invalid = raw === '' || !isFinite(Number(raw)) || Number(raw) < 0;
                const showHint = rateGate.attempted && invalid;
                return (
                <div key={p.id} className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-slate-800">{p.name}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-slate-400 font-bold text-xs">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={rateGate.amounts[p.id] ?? ''}
                        onChange={e => setRateGate(prev => prev ? { ...prev, amounts: { ...prev.amounts, [p.id]: e.target.value } } : prev)}
                        placeholder="0.00"
                        aria-label={`Commission amount for ${p.name}`}
                        aria-invalid={showHint}
                        className={`w-24 px-2 py-1.5 bg-white border rounded-lg text-right text-xs font-bold text-slate-900 focus:outline-none focus:ring-1 ${showHint ? 'border-rose-300 focus:ring-rose-400' : 'border-slate-200 focus:ring-brand-primary'}`}
                      />
                    </div>
                  </div>
                  {showHint && (
                    <p className="text-[10px] font-semibold text-rose-600 mt-1.5">Amount required — enter $0 if no commission is earned for this program.</p>
                  )}
                </div>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setRateGate(null)}
                className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl text-xs font-semibold transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitRateGate}
                disabled={!allValid || approving === `referrer-${rateGate.id}`}
                title={allValid ? 'Save the amounts and approve this referrer' : 'Enter an amount for every program above first'}
                className="px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-xs font-bold transition cursor-pointer"
              >
                Save amounts &amp; approve
              </button>
            </div>
          </div>
      </Modal>
        );
      })()}
    </div>
  );
}
